import React, { useState } from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

interface CellImageProps {
  params: any;
  onClick?: () => void;
}

interface CellImageComponentProps {
  children: string;
}

// Main component that receives params from DataGrid
const CellImage = ({ params }: CellImageProps) => {
  const imageUrl = params?.value || '';
  
  return <CellImageComponent>{imageUrl}</CellImageComponent>;
};

// Actual image component that handles the rendering
const CellImageComponent = ({ children: imageUrl }: CellImageComponentProps) => {
  const [imageError, setImageError] = useState(false);

  // Helper function to truncate URL for tooltip
  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    return `${url.substring(0, maxLength)}...`;
  };

  // Handle image click to open in new tab
  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent row click and other event handlers
    
    if (imageUrl && imageUrl.trim() !== '') {
      try {
        window.open(imageUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.warn('Error opening image in new tab:', error);
      }
    }
  };

  if (!imageUrl || imageUrl.trim() === '') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          cursor: 'default',
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'grey.200',
          }}
        >
          <ImageIcon sx={{ color: 'grey.500', fontSize: 16 }} />
        </Avatar>
      </Box>
    );
  }

  return (
    <Tooltip 
      title={truncateUrl(imageUrl, 60)} 
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 400,
            fontSize: '0.75rem',
            wordBreak: 'break-all'
          }
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          cursor: 'pointer',
          position: 'relative',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
          }
        }}
        onClick={handleImageClick}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Avatar
          src={imageUrl}
          sx={{
            width: 32,
            height: 32,
            border: '1px solid',
            borderColor: 'grey.300',
            '& img': {
              objectFit: 'cover',
            },
          }}
          onError={() => setImageError(true)}
        >
          <ImageIcon sx={{ color: 'grey.500', fontSize: 16 }} />
        </Avatar>
        
        {/* External link indicator */}
        {!imageError && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              '.MuiBox-root:hover &': {
                opacity: 1
              }
            }}
          >
            <Box
              sx={{
                color: 'primary.contrastText',
                fontSize: '8px',
                fontWeight: 'bold',
                lineHeight: 1
              }}
            >
              ↗
            </Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default CellImage;
